'use client'

import { useState, useEffect } from 'react'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUITypography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'
import MUIStack from '@mui/material/Stack'
import { PlayPauseIcon } from '@repo/ui/PlayPauseIcon'
import { TimeRecordingTimeDisplay } from '../TimeRecordingTimeDisplay/TimeRecordingTimeDisplay'
import styles from './TimeRecordingHeaderWidget.module.scss'
import { useMattersStore } from '@repo/store/useMattersStore'
import { ClientNameAndMatterDescription } from '@repo/ui/ClientNameAndMatterDescription'

export const TimeRecordingHeaderWidget = () => {
  const { setTimeRecordingDrawerIsOpen, activeTimer, pauseTimer, resumeTimer, fetchActiveTimer } =
    useTimeRecordingStore()
  const { clientNo, matterNo, matterDescription, isPlaying } = activeTimer || {}
  const clientMatterNo = `${clientNo}-${matterNo}`
  const { matters, fetchMatter } = useMattersStore()

  const [timerIsPlaying, setTimerIsPlaying] = useState(isPlaying)
  const [timerMatter, setTimerMatter] = useState(matters[clientMatterNo])

  useEffect(() => {
    fetchActiveTimer()
  }, [fetchActiveTimer])

  useEffect(() => {
    const matter = matters[clientMatterNo]
    if (matter) setTimerMatter(matter)
    else if (activeTimer && clientNo) fetchMatter({ clientMatterNo })
    else setTimerMatter(null)
    // eslint-disable-next-line
  }, [clientMatterNo, matters])

  useEffect(() => {
    setTimerIsPlaying(activeTimer?.isPlaying || false)
  }, [activeTimer, activeTimer?.isPlaying])

  const handleIconClick = (e) => {
    e.preventDefault()

    // update local state so the play/pause icon updates immediately
    setTimerIsPlaying(!timerIsPlaying)

    // local state will be overridden once the following API calls complete
    if (timerIsPlaying) pauseTimer(activeTimer)
    else resumeTimer(activeTimer)
  }

  const renderClientName = () => {
    if (!activeTimer) return 'No timer selected'
    if (!timerMatter) return `Blank matter`

    const { forename, surname, clientName } = timerMatter
    if (forename && surname) return `${forename} ${surname}`
    if (surname) return surname
    if (clientName) return clientName
    return ''
  }

  return (
    <div style={{ position: 'relative' }}>
      <MUIButton
        variant="contained"
        sx={{
          backgroundColor: 'background.dark',
          borderRadius: 20,
          color: 'black',
          height: '42px',
          width: 'auto',
          padding: '7px 12px 3px 47px',
          '&:hover': {
            backgroundColor: 'background.dark',
            filter: 'brightness(1.02)'
          }
        }}
        mt="-6px"
        data-testid="TimeRecordingHeaderWidget"
        onClick={() => setTimeRecordingDrawerIsOpen(true)}
        className={activeTimer && timerIsPlaying ? styles.pulse : ''}
      >
        <MUIStack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ textTransform: 'none', textAlign: 'left' }}
        >
          {activeTimer && clientNo ? (
            <ClientNameAndMatterDescription
              clientNo={clientNo}
              matterNo={Number(matterNo)}
              surname={renderClientName()}
              matterDescription={matterDescription}
              sx={{ maxWidth: '220px', marginTop: '0px' }}
            />
          ) : null}

          {!clientNo ? (
            <MUITypography variant="body2" sx={{ color: 'primary.main', width: '220px', mb: 0 }}>
              {activeTimer ? 'Blank matter' : 'No timer selected'}
            </MUITypography>
          ) : null}

          {activeTimer ? (
            <TimeRecordingTimeDisplay
              isTimer
              timerIsPlaying={timerIsPlaying}
              entry={activeTimer}
              sx={{ textAlign: 'right', color: 'primary.main', minWidth: '50px' }}
              variant="body2"
              syncTime
            />
          ) : null}
        </MUIStack>
      </MUIButton>

      <MUITooltip
        title={activeTimer ? (timerIsPlaying ? 'Pause timer' : 'Resume timer') : 'Time recording options'}
        placement="bottom"
        arrow
      >
        <MUIIconButton
          sx={{
            position: 'absolute',
            left: 0,
            width: '42px',
            height: '42px',
            padding: '4px 12px',
            pointerEvents: activeTimer ? 'all' : 'none',
            backgroundColor: 'background.dark',
            transition: 'background-color 0.3s',
            '&:hover': {
              backgroundColor: timerIsPlaying ? 'background.dark' : 'secondary.light'
            }
          }}
          onClick={handleIconClick}
        >
          <PlayPauseIcon sx={{ width: 36, height: 36 }} isPlaying={activeTimer ? timerIsPlaying : false} />
        </MUIIconButton>
      </MUITooltip>
    </div>
  )
}
